/**
 * Default style configuration for the checkout widget
 * These values match the STYLE_CUSTOMIZATION_SCHEMA.md defaults
 */
export const getDefaultStyles = () => ({
  colors: {
    // Primary Colors
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    primaryLight: '#eaf3ff',
    secondary: '#64748b',
    accent: '#0ea5e9',
    
    // Background Colors
    pageBackground: '#ffffff',
    containerBackground: '#ffffff',
    drawerBackground: '#f7fafd',
    inputBackground: '#ffffff',
    inputBackgroundHover: '#f9fafb',
    inputBackgroundFocus: '#ffffff',
    selectedBackground: '#eaf3ff',
    
    // Text Colors
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textLabel: '#1f2937',
    textPlaceholder: '#9ca3af',
    textOnPrimary: '#ffffff',
    textError: '#dc2626',
    textSuccess: '#059669',
    
    // Border Colors
    border: '#e5e7eb',
    borderHover: '#d1d5db',
    borderFocus: '#2563eb',
    borderSelected: '#2563eb',
    borderError: '#dc2626',
    divider: '#e3e8f0',
    
    // Status Colors
    error: '#dc2626',
    errorLight: '#fee2e2',
    success: '#059669',
    successLight: '#d1fae5',
    warning: '#d97706',
    warningLight: '#fef3c7',
    info: '#0284c7',
    infoLight: '#e0f2fe',
  },
  
  typography: {
    fontFamily: "'Arial', sans-serif",
    fontFamilyHeadings: "'Arial', sans-serif",
    fontFamilyMonospace: "'Courier New', monospace",
    fontSizes: {
      xs: '12px',
      sm: '13px',
      base: '14px',
      md: '15px',
      lg: '16px',
      xl: '17px',
      '2xl': '18px',
      '3xl': '20px',
    },
    fontWeights: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
    letterSpacing: {
      tight: '-0.01em',
      normal: '0',
      wide: '0.01em',
      wider: '0.5px',
    },
  },
  
  spacing: {
    padding: {
      container: '30px',
      containerMobile: '20px',
      paymentOption: '16px 20px',
      drawer: '16px 20px',
      input: '10px 12px',
      button: '14px 24px',
      badge: '3px 14px',
    },
    margin: {
      sectionBottom: '30px',
      elementBottom: '12px',
      labelBottom: '6px',
    },
    gap: {
      paymentOptions: '18px',
      formElements: '12px',
      icons: '10px',
    },
  },
  
  borderRadius: {
    small: '6px',
    medium: '8px',
    large: '12px',
    xlarge: '14px',
    round: '50%',
    pill: '999px',
  },
  
  borderWidth: {
    thin: '1px',
    default: '1.5px',
    thick: '2px',
    highlight: '5px',
  },
  
  borderStyle: {
    default: 'solid',
    dashed: 'dashed',
    none: 'none',
  },
  
  shadows: {
    none: 'none',
    small: '0 1px 2px rgba(0, 0, 0, 0.05)',
    default: '0 2px 10px rgba(0, 0, 0, 0.05)',
    medium: '0 2px 12px rgba(60, 90, 130, 0.08)',
    large: '0 4px 24px rgba(60, 90, 130, 0.14)',
    xlarge: '0 8px 16px rgba(0, 0, 0, 0.1)',
    focus: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    badge: '0 1px 4px rgba(37,99,235,0.08)',
  },
  
  transitions: {
    fast: '0.1s ease',
    default: '0.2s ease',
    slow: '0.3s ease',
    properties: {
      all: 'all',
      colors: 'background-color, border-color, color',
      shadow: 'box-shadow',
      transform: 'transform',
    },
  },
  
  components: {
    paymentOption: {
      borderRadius: '14px',
      shadow: '0 2px 12px rgba(60, 90, 130, 0.08)',
      shadowHover: '0 4px 24px rgba(60, 90, 130, 0.14)',
      backgroundColor: '#ffffff',
      selectedBackgroundColor: '#eaf3ff',
      selectedBorderColor: '#2563eb',
      selectedBorderWidth: '5px',
    },
    radioButton: {
      size: '22px',
      innerSize: '12px',
      borderWidth: '2px',
      borderColor: '#bfc9d9',
      borderColorSelected: '#2563eb',
      backgroundColor: '#ffffff',
      innerColorSelected: '#2563eb',
    },
    input: {
      borderRadius: '6px',
      borderWidth: '1.5px',
      borderColor: '#e5e7eb',
      borderColorHover: '#d1d5db',
      borderColorFocus: '#2563eb',
      backgroundColor: '#ffffff',
      backgroundColorHover: '#f9fafb',
      backgroundColorFocus: '#ffffff',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '400',
      textColor: '#111827',
      placeholderColor: '#9ca3af',
      focusShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    button: {
      primary: {
        backgroundColor: '#0E1A2D',
        backgroundColorHover: '#1a2d47',
        backgroundColorDisabled: '#aaaaaa',
        textColor: '#ffffff',
        borderRadius: '8px',
        padding: '14px 24px',
        fontSize: '16px',
        fontWeight: '600',
        transition: '0.3s ease',
      },
    },
    badge: {
      backgroundColor: '#2563eb',
      textColor: '#ffffff',
      fontSize: '13px',
      fontWeight: '600',
      borderRadius: '999px',
      padding: '3px 14px',
      letterSpacing: '0.5px',
      shadow: '0 1px 4px rgba(37,99,235,0.08)',
    },
    label: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '6px',
      letterSpacing: '0.01em',
    },
    errorMessage: {
      color: '#dc2626',
      fontSize: '12px',
      fontWeight: '500',
      marginTop: '6px',
    },
    loader: {
      size: '40px',
      borderWidth: '4px',
      borderColor: 'rgba(79, 70, 229, 0.2)',
      borderTopColor: '#0E1A2D',
      animationDuration: '1s',
    },
    paymentIcon: {
      height: '28px',
      width: 'auto',
      gap: '10px',
    },
  },
  
  layout: {
    maxWidth: '1200px',
    containerWidth: '100%',
    leftSectionWidth: 'flex: 1',
    rightSectionWidth: '400px',
  },
  
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
  },
});

/**
 * Helper function to extract style customization from session response
 * Checks multiple possible locations in the response
 */
export const extractStyleCustomization = (sessionResponse) => {
  // Check if it's at data.styleCustomization (original location)
  if (sessionResponse?.data?.styleCustomization) {
    return sessionResponse.data.styleCustomization;
  }
  
  // Check if it's at data.checkoutProcessDto.styleCustomization (new location)
  if (sessionResponse?.data?.checkoutProcessDto?.styleCustomization) {
    return sessionResponse.data.checkoutProcessDto.styleCustomization;
  }
  
  return null;
};

/**
 * NEW SIMPLIFIED SYSTEM - Only 9 parameters
 * Maps merchant-friendly parameters to internal structure
 */
export const normalizeSimplifiedStyles = (styles) => {
  if (!styles) return null;
  
  const {
    backgroundColor,
    primaryColor,
    secondaryColor,
    accentColor,
    borderRadius,
    headerTextColor,
    textColor,
    fontFamily,
    dropShadow
  } = styles;
  
  const normalized = {};
  
  // 1. backgroundColor - Page background
  if (backgroundColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.pageBackground = backgroundColor;
  }
  
  // 2. primaryColor - Payment component banner & input borders (default state)
  if (primaryColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.primary = primaryColor;
    normalized.colors.selectedBackground = primaryColor; // Banner uses solid primaryColor
    
    normalized.components = normalized.components || {};
    normalized.components.input = normalized.components.input || {};
    normalized.components.input.borderColor = primaryColor;
    normalized.components.input.borderColorHover = primaryColor;
    
    normalized.components.paymentOption = normalized.components.paymentOption || {};
    normalized.components.paymentOption.selectedBackgroundColor = primaryColor; // Solid color, not transparent
  }
  
  // 3. secondaryColor - Payment component content background & input backgrounds
  if (secondaryColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.drawerBackground = secondaryColor;
    normalized.colors.containerBackground = secondaryColor;
    
    normalized.components = normalized.components || {};
    normalized.components.input = normalized.components.input || {};
    normalized.components.input.backgroundColor = secondaryColor;
    normalized.components.input.backgroundColorHover = secondaryColor;
    normalized.components.input.backgroundColorFocus = secondaryColor;
    
    normalized.components.paymentOption = normalized.components.paymentOption || {};
    normalized.components.paymentOption.backgroundColor = secondaryColor;
  }
  
  // 4. accentColor - Radio button toggle, buttons, selected states, focus borders
  if (accentColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.borderSelected = accentColor;
    normalized.colors.borderFocus = accentColor;
    normalized.colors.accentPrimary = accentColor;  // For radio button
    
    normalized.components = normalized.components || {};
    
    // Selected left border
    normalized.components.paymentOption = normalized.components.paymentOption || {};
    normalized.components.paymentOption.selectedBorderColor = accentColor;
    
    // Radio button - uses accentColor for toggle
    normalized.components.radioButton = normalized.components.radioButton || {};
    normalized.components.radioButton.borderColorSelected = accentColor;
    normalized.components.radioButton.innerColorSelected = accentColor;
    
    // Input focus border
    normalized.components.input = normalized.components.input || {};
    normalized.components.input.borderColorFocus = accentColor;
    
    // Buttons
    normalized.components.button = normalized.components.button || {};
    normalized.components.button.primary = normalized.components.button.primary || {};
    normalized.components.button.primary.backgroundColor = accentColor;
    normalized.components.button.primary.backgroundColorHover = accentColor;
    
    // Badge
    normalized.components.badge = normalized.components.badge || {};
    normalized.components.badge.backgroundColor = accentColor;
  }
  
  // 5. borderRadius - All components and inputs
  if (borderRadius && typeof borderRadius === 'string') {
    normalized.borderRadius = {
      small: borderRadius,   // Inputs
      medium: borderRadius,  // Buttons
      large: borderRadius,   // Containers
      xlarge: borderRadius,  // Payment cards
      round: '50%',          // Radio buttons (always round)
      pill: '999px',         // Badges (always pill)
    };
  }
  
  // 6. headerTextColor - Payment method headers (UPI, Card, Pix)
  if (headerTextColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.textPrimary = headerTextColor;
    
    normalized.typography = normalized.typography || {};
    normalized.typography.fontWeights = normalized.typography.fontWeights || {};
  }
  
  // 7. textColor - All content text, labels, input text, placeholder
  if (textColor) {
    normalized.colors = normalized.colors || {};
    normalized.colors.textLabel = textColor;
    normalized.colors.textSecondary = textColor;
    
    normalized.components = normalized.components || {};
    normalized.components.input = normalized.components.input || {};
    normalized.components.input.textColor = textColor;
    normalized.components.input.placeholderColor = textColor + '80'; // 50% opacity for placeholder
    
    normalized.components.label = normalized.components.label || {};
    normalized.components.label.color = textColor;
  }
  
  // 8. fontFamily - All text
  if (fontFamily) {
    normalized.typography = normalized.typography || {};
    normalized.typography.fontFamily = fontFamily;
    normalized.typography.fontFamilyHeadings = fontFamily;
  }
  
  // 9. dropShadow - Payment component shadow (boolean)
  if (typeof dropShadow === 'boolean') {
    normalized.components = normalized.components || {};
    normalized.components.paymentOption = normalized.components.paymentOption || {};
    normalized.components.paymentOption.shadow = dropShadow ? '0 2px 12px rgba(60, 90, 130, 0.08)' : 'none';
    normalized.components.paymentOption.shadowHover = dropShadow ? '0 4px 24px rgba(60, 90, 130, 0.14)' : 'none';
  }
  
  return normalized;
};

